import { computed, ref, type Ref, watch } from 'vue';
import type { Insight, InsightAxis } from '@/types/insights';
import { useSwrPolling } from '@/utils/composables/useSwrPolling';
import {
  hasProcessingAxis,
  insightPayloadAxes,
} from '@/utils/insights/insightAxis';
import { useInsightRecommendedActionPrefillPromptStatuses } from '@/utils/swr';

export const PREFILL_PROMPT_STATUSES_POLLING_INTERVAL_MS = 2000;

const processingActionUuids = (axis: InsightAxis): string[] =>
  axis.generation_status === 'processed'
    ? axis.hints.flatMap(hint =>
        hint.recommended_actions
          .filter(
            action => action.prefill_prompt_generation_status === 'processing',
          )
          .map(action => action.uuid),
      )
    : [];

export const useInsightPrefillPromptStatuses = (
  insight: Ref<Insight | undefined>,
) => {
  const snapshotProcessingUuids = computed(() => {
    const payload = insight.value?.payload;
    if (payload === undefined) return [];
    return insightPayloadAxes(payload).flatMap(processingActionUuids);
  });
  const snapshotHasProcessing = computed(
    () => snapshotProcessingUuids.value.length > 0,
  );

  const enabled = ref(false);
  const { data: statuses, mutate } =
    useInsightRecommendedActionPrefillPromptStatuses(enabled);

  const anyProcessing = computed(
    () =>
      statuses.value?.some(
        status => status.prefill_prompt_generation_status === 'processing',
      ) ?? false,
  );

  /**
   * レスポンスに無いアクションは、そのレスポンスが軸の公開より前に取得されたものなので、
   * 未解決として扱いポーリングを続ける。
   * レスポンスで processed / error になっていれば、止まったスナップショットが processing のままでも解決済みとする。
   */
  const unresolvedInResponse = computed(() => {
    const statusByUuid = new Map(
      (statuses.value ?? []).map(status => [
        status.uuid,
        status.prefill_prompt_generation_status,
      ]),
    );
    return snapshotProcessingUuids.value.some(uuid => {
      const status = statusByUuid.get(uuid);
      return status === undefined || status === 'processing';
    });
  });

  const shouldPoll = computed(() => {
    // 遅延到着する軸が後から processing のアクションを持ち込むので、生成中の軸があるうちは止めない。
    const hasUnsettledAxis = hasProcessingAxis(insight.value?.payload);
    return (
      enabled.value &&
      (anyProcessing.value || unresolvedInResponse.value || hasUnsettledAxis)
    );
  });

  watch(
    snapshotHasProcessing,
    hasProcessing => {
      if (hasProcessing && !enabled.value) {
        enabled.value = true;
      }
    },
    { immediate: true },
  );

  useSwrPolling(
    mutate,
    shouldPoll,
    PREFILL_PROMPT_STATUSES_POLLING_INTERVAL_MS,
  );

  return {
    statuses: computed(() => statuses.value ?? []),
  };
};
